import { ProductInterface } from "../interfaces/ProductInterface";
import { Image } from "../interfaces/ProductDetailsDataInterface";
import { BrandingInterface } from "../interfaces/BrandingInterface";

const resolveCdnImageUrl = (
    image?:
        | Pick<Image, "url" | "converted">
        | ProductInterface["productImages"][number],
): string | undefined => {
    if (!image) {
        return undefined;
    }

    const converted = image.converted;
    const candidates = [
        converted?.HD,
        converted?.Large,
        converted?.Product,
        converted?.Medium,
        converted?.Small,
        converted?.QHD,
        image.url,
    ];

    return candidates.find(
        (candidate): candidate is string =>
            typeof candidate === "string" && candidate.length > 0,
    );
};

export const loadProductImages = async (
    products: ProductInterface[],
    getLocalImageUrl: (cdnUrl: string) => Promise<string | null>,
): Promise<{ [key: string]: string }> => {
    const imageUrls: { [key: string]: string } = {};

    for (const product of products) {
        if (!product.productImages?.length) {
            continue;
        }

        const cdnUrl = resolveCdnImageUrl(product.productImages[0]);
        if (!cdnUrl) {
            continue;
        }

        try {
            const localUrl = await getLocalImageUrl(cdnUrl);
            imageUrls[product.id] = localUrl ?? "";
        } catch (_error) {
            imageUrls[product.id] = "";
        }
    }

    return imageUrls;
};

export const convertProductImagesToLocal = async (
    productImages: Image[],
    getLocalImageUrl: (cdnUrl: string) => Promise<string | null>,
): Promise<Image[]> => {
    const convertedImages: Image[] = [];

    for (const productImage of productImages) {
        const cdnUrl = resolveCdnImageUrl(productImage);
        if (!cdnUrl) {
            continue;
        }

        try {
            const localUrl = await getLocalImageUrl(cdnUrl);
            if (localUrl) {
                convertedImages.push({
                    ...productImage,
                    url: localUrl,
                });
            }
        } catch (_error) {
            // Skip images that fail to load from local storage
        }
    }

    return convertedImages;
};

export const loadBrandingFiles = async (
    brandingItems: BrandingInterface[],
    getLocalBrandingFileUrl: (url: string) => Promise<string | null>,
): Promise<{ [key: string]: string }> => {
    const imageUrls: { [key: string]: string } = {};

    for (const brandingItem of brandingItems) {
        if (brandingItem.mainUrl) {
            const cdnUrl = brandingItem.mainUrl;
            try {
                const localUrl = await getLocalBrandingFileUrl(cdnUrl);
                imageUrls[brandingItem.id] = localUrl ?? "";
            } catch (_error) {
                imageUrls[brandingItem.id] = "";
            }
        }
    }

    return imageUrls;
};

export const loadBrandingThumbnails = async (
    brandingItems: BrandingInterface[],
    getLocalBrandingFileUrl: (url: string) => Promise<string | null>,
): Promise<{ [key: string]: string }> => {
    const thumbnailUrls: { [key: string]: string } = {};

    for (const brandingItem of brandingItems) {
        if (brandingItem.thumbnailUrl) {
            const cdnUrl = brandingItem.thumbnailUrl;
            try {
                const localUrl = await getLocalBrandingFileUrl(cdnUrl);
                thumbnailUrls[brandingItem.id] = localUrl ?? "";
            } catch (_error) {
                thumbnailUrls[brandingItem.id] = "";
            }
        }
    }

    return thumbnailUrls;
};
